import {
  AnchorButton,
  Button,
  Field,
  Form,
  PasswordInput,
  RawInput,
} from '@payaca/ambrosia';
import { useMutation } from '@tanstack/react-query';
import { FC, useState, type JSX } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';

import { REFRESH_TOKEN_KEY, TOKEN_KEY } from '@/api';
import { gqlClient } from '@/api/graphql-client';
import { LoginMutationVariables } from '@/gql/graphql';
import { getNewWorldTokenLocalStorageKey } from '@/helpers/localStorageKeyHelper';

import { graphql } from '@/gql';

import * as Sentry from '@sentry/react';
import { Controller, useForm } from 'react-hook-form';

import { actions as appActions } from '@/api/app';
import { zodResolver } from '@hookform/resolvers/zod';

import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';
import './LoginForm.sass';

type Props = {
  hideForgotPassword: boolean;
  onLoginCallback?: () => void;
};

type LoginFormState = z.infer<typeof LOGIN_FORM_SCHEMA>;

const LOGIN_FORM_SCHEMA = z.object({
  emailAddress: z.email({
    error: 'Enter a valid email including "@" and domain',
  }),
  password: z.string().min(1, 'Password is required'),
});

const LoginMutation = graphql(`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      accessToken
    }
  }
`);

const LoginForm: FC<Props> = ({
  hideForgotPassword,
  onLoginCallback,
}: Props): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();

  const formMethods = useForm({
    resolver: zodResolver(LOGIN_FORM_SCHEMA),
    defaultValues: {
      emailAddress: '',
      password: '',
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (variables: LoginMutationVariables) => {
      return gqlClient.request(LoginMutation, variables);
    },
  });

  const onSubmit = async (data: LoginFormState) => {
    try {
      const result = await loginMutation.mutateAsync({
        email: data.emailAddress,
        password: data.password,
      });

      const token = result.login.accessToken;
      localStorage.setItem(getNewWorldTokenLocalStorageKey(), token);
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REFRESH_TOKEN_KEY, token);

      // trigger rerender
      dispatch(appActions.start());

      onLoginCallback?.();
      history.push('/');
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null) {
        const maybeError = error as { response?: { errors?: any[] } };

        const firstError = maybeError.response?.errors?.[0];

        const isBadCredentials =
          firstError?.message === 'Unauthorized' &&
          firstError?.path?.[0] === 'login';

        if (!isBadCredentials) {
          Sentry.captureException(error);
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('Login failed:', error);
        }

        return;
      }

      Sentry.captureException(error);
    }
  };

  return (
    <div className="login-form-container">
      <Form
        methods={formMethods}
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        <Controller
          name="emailAddress"
          render={({ field: { value, onChange, onBlur, name } }) => (
            <Field name={name}>
              <Field.Label>Email</Field.Label>

              <RawInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                type="email"
                placeholder="Email address"
                id="login-email"
              />
            </Field>
          )}
        />

        <Controller
          name="password"
          render={({ field: { value, onChange, onBlur, name } }) => (
            <Field name={name}>
              <Field.Label>Password</Field.Label>

              <PasswordInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="Enter your password"
                id="password"
              />
            </Field>
          )}
        />
        <div className="flex justify-between">
          {!hideForgotPassword && (
            <div className="flex-start">
              <AnchorButton
                as={Link}
                variant={'link-inline'}
                to="/login/forgotPassword"
              >
                Forgotten password?
              </AnchorButton>
            </div>
          )}
          <div className="button-wrapper flex-end">
            <Button
              type="submit"
              isProcessing={loginMutation.isPending}
              variant="solid"
              color="blue"
            >
              Log in
            </Button>
          </div>
        </div>
        {loginMutation.isError && (
          <ErrorMessage
            className="text-normal"
            message="Email or password not recognised."
          />
        )}
      </Form>
    </div>
  );
};

export default LoginForm;
